/**
 * Typ nástroje mapového editoru.
 */
enum EditorToolType {
    /**
     * Zakreslení bodu.
     */
    POINT = 'POINT',

    /**
     * Zakreslení bodu zadáním souřadnic.
     */
    POINT_COORDS = 'POINT_COORDS',

    /**
     * Zakreslení lomené čary.
     */
    LINE = 'LINE',

    /**
     * Zakreslení lomené čáry zadáním souřadnic.
     */
    LINE_COORDS = 'LINE_COORDS',

    /**
     * Zakreslení lomené čáry zadáním offsetu k jiné lomené čáře.
     */
    LINE_OFFSET = 'LINE_OFFSET',

    /**
     * Zakreslení lomené čáry vybráním dvou bodů na jiné lomené čáře a zadáním offsetu.
     */
    LINE_PARTIAL_OFFSET = 'LINE_PARTIAL_OFFSET',

    /**
     * Zakreslení rozšíření k lomené čáře z výběru.
     */
    LINE_EXTENSION = 'LINE_EXTENSION',

    /**
     * Zakreslení čáry a zadání její kolmice.
     */
    PERPENDICULAR_LINE = 'PERPENDICULAR_LINE',

    /**
     * Zakreslení polygonu.
     */
    POLYGON = 'POLYGON',

    /**
     * Zakreslení polygonu zadáním souřadnic.
     */
    POLYGON_COORDS = 'POLYGON_COORDS',

    /**
     * Zakreslení obdélníku o pevné velikosti zadáním rozměrů.
     */
    FIXED_RECTANGLE = 'FIXED_RECTANGLE',

    /**
     * Zakreslení kruhu.
     */
    CIRCLE = 'CIRCLE',

    /**
     * Zakreslení středu kruhu s následným zadání poloměru.
     */
    CIRCLE_RADIUS = 'CIRCLE_RADIUS',

    /**
     * Zakreslení kruhu zadání souřadnic a poloměru.
     */
    CIRCLE_COORDS = 'CIRCLE_COORDS',

    /**
     * Zakreslení lomené čáry s vodícími čárami (pro zakreslení kolmic a rovnoběžek).
     */
    GUIDED_LINE = 'GUIDED_LINE',

    /**
     * Zakreslení polygonu s vodícími čárami (pro zakreslení kolmic a rovnoběžek).
     */
    GUIDED_POLYGON = 'GUIDED_POLYGON',

    /**
     * Měření vzdálenosti zakreslením čáry.
     */
    MEASURE_LINE = 'MEASURE_LINE',

    /**
     * Měření plochy zakreslením polygonu.
     */
    MEASURE_POLYGON = 'MEASURE_POLYGON',

    /**
     * Výběr objektů kliknutím.
     */
    SELECT = 'SELECT',

    /**
     * Přesný výběr objektů kliknutím.
     * Pokud se pod kurzorem nachází více objektů, je zobrazen výběrový dialog.
     */
    EXACT_SELECT = 'EXACT_SELECT',

    /**
     * Přesný výběr (jednoho) objektu kliknutím.
     * Pokud se pod kurzorem nachází více objektů, je zobrazen výběrový dialog.
     *
     * Narozdíl od {@link EXACT_SELECT} nelze naklikat postupně více objektů najednou
     * držením klávesy SHIFT. Výsledný výběr bude vždy maximálně jeden objekt.
     */
    EXACT_SINGLE_SELECT = 'EXACT_SINGLE_SELECT',

    /**
     * Výběr objektů zakreslením obdélníku.
     */
    BOX_SELECT = 'BOX_SELECT',

    /**
     * Výběr objektů zakreslením lomené čáry.
     */
    LINE_SELECT = 'LINE_SELECT',

    /**
     * Výběr objektů zakreslením polygonu.
     */
    POLY_SELECT = 'POLY_SELECT',

    /**
     * Výběr objektů protínajících existující polygon (feature).
     */
    POLY_SELECT_INTERSECT = 'POLY_SELECT_INTERSECT',

    /**
     * Výběr objektů zakreslením kružnice.
     */
    CIRCLE_SELECT = 'CIRCLE_SELECT',

    /**
     * Zoom zakreslením obdélníku.
     */
    BOX_ZOOM = 'BOX_ZOOM',

    /**
     * Posuv vybraných features.
     * Umožňuje zároveň i výběr kliknutím.
     */
    MOVE = 'MOVE',

    /**
     * Úprava vybraných features.
     * Umožňuje zároveň i výběr kliknutím.
     */
    EDIT = 'EDIT',

    /**
     * Úprava a posuv vybraných features.
     * Umožňuje zároveň i výběr kliknutím.
     */
    EDIT_MOVE = 'EDIT_MOVE',

    /**
     * Rotace vybrané feature.
     * Umožňuje zároveň i výběr kliknutím.
     */
    ROTATE = 'ROTATE',

    /**
     * Rotace a posuv vybrané feture.
     * Umožňuje zároveň i výběr kliknutím.
     */
    ROTATE_MOVE = 'ROTATE_MOVE',

    /**
     * Vyjmutí vybraných features do schránky.
     */
    CUT = 'CUT',

    /**
     * Zkopírování vybraných features do schránky.
     */
    COPY = 'COPY',

    /**
     * Vložení features ze schránky.
     */
    PASTE = 'PASTE',

    /**
     * Smazání vybraných features.
     */
    DELETE = 'DELETE',

    /**
     * Zduplikování vybraných features.
     */
    DUPLICATE = 'DUPLICATE',

    /**
     * Spojení vybraných features do jedné.
     * Geometrie výsledné feature bude typu `GeometryCollection`.
     */
    MERGE = 'MERGE',

    /**
     * Spojení vybraných čar (dotýkajících se koncovými body) do jedné.
     */
    MERGE_LINES = 'MERGE_LINES',

    /**
     * Rozpojení vybrané features do více.
     * Vybraná feature musí mít geometrii typu `GeometryCollection`.
     */
    SPLIT = 'SPLIT',

    /**
     * Rozdělení čáry na dvě ve vybraném místě.
     */
    SPLIT_LINE = 'SPLIT_LINE',

    /**
     * Rozdělení polygonu zakreslením čáry.
     */
    SPLIT_POLYGON = 'SPLIT_POLYGON',

    /**
     * Vytvoření offsetu vybrané lomené čáry nebo polygonu.
     */
    OFFSET = 'OFFSET',

    /**
     * Režim přidávání bufferu k features.
     */
    BUFFER = 'BUFFER',

    /**
     * Režim přichytávání k ostatním features během zakreslování a editace.
     */
    SNAP = 'SNAP',

    /**
     * Režim dočasné editace vybraných read-only features.
     *
     * Editace je ohraničena událostmi {@link EditorTempEditingStartedEvent} a
     * {@link EditorTempEditingFinishedEvent}. Více features lze najednou editovat
     * pouze, pokud jsou ze stejné skupiny (mají identickou property `groupId`).
     * Je určeno pouze pro režim {@link EditorLayersMode.OVERLAY}.
     */
    TEMP_EDITING = 'TEMP_EDITING',

    /**
     * Zobrazení markeru a souřadnic po kliknutí do mapy.
     */
    SHOW_COORDS = 'SHOW_COORDS',

    /**
     * Zobrazení markeru a podrobností o dané lokaci po kliknutí do mapy.
     */
    SHOW_LOCATION = 'SHOW_LOCATION',

    /**
     * Zobrazení propojení features na okolní místa.
     * Musí být podporováno danou datovou vrstvou.
     */
    SHOW_CONNECTIONS = 'SHOW_CONNECTIONS',

    /**
     * Vycentrování středu mapy na výchozí polohu.
     */
    ORIGIN_LOCATION = 'ORIGIN_LOCATION',

    /**
     * Vyhledání informací o objektech po kliknutí do mapy.
     */
    INSPECT = 'INSPECT',

    /**
     * Zobrazení tiskového dialogu.
     */
    PRINT = 'PRINT',

    /**
     * Získání aktuální pozice pomocí geolokace.
     *
     * Získaná pozice je vyznačena do mapy v podobě markeru a je provedeno
     * vycentrování na tuto pozici. Po dokončení geolokace je vyvolána
     * událost {@link EditorGeolocationEvent}.
     */
    GEOLOCATION = 'GEOLOCATION',

    /**
     * Export vybraných features do souboru.
     */
    EXPORT_FEATURES = 'EXPORT_FEATURES',

    /**
     * Export vrstev do souboru.
     */
    EXPORT_LAYERS = 'EXPORT_LAYERS',

    /**
     * Definice vlastní akce.
     * Její provedení vyvolá událost {@link EditorCustomActionEvent}.
     */
    CUSTOM_ACTION = 'CUSTOM_ACTION',

    /**
     * Jednoúrovňové nástrojové menu.
     */
    MENU = 'MENU',
}

export default EditorToolType;
